/* The quote HEADER draft, and the rules for what may be typed into it.
 *
 * In its own module rather than beside any one form, because three surfaces now
 * ask these same questions: the sheet the detail page edits a header in
 * (QuoteFormSheet), step 1 of the stepped builder (QuoteIdentityStep), and the
 * builder's own header disclosures (QuoteHeaderExtras), which carry the rest of
 * the FR-4 fields on the steps that own their subjects. Copies of these
 * predicates would eventually disagree about whether "20 " is a number, and one
 * surface would send what another refused.
 *
 * A component file may only export components (react-refresh), which is the
 * mechanical reason this is not in one of them — and the same reason
 * toQuotePayload sits beside its mutation rather than beside its form.
 */

import type { QuoteDetail } from '@/types/domain'

export interface QuoteDraft {
  clientId: string
  /* FR-4's "customer AND contact": which person at the client the offer is
   * addressed to.
   *
   * An id, not free text — a pick from the client's own directory contacts,
   * which is the substitution FR-39 exists to make. Blank is legitimate and is
   * the common case: with no contact named, the server addresses the offer to
   * the client's first contact carrying an email, and the detail read shows the
   * same one through the same function (quote.firstAddressable) so the address
   * SHOWN and the address SENT TO cannot come from two rules. */
  clientContactId: string
  quoteDate: string
  validUntil: string
  /* FR-4's header cargo, which is not the FR-12 positions.
   *
   * `cargoOwner` is whose goods these are — routinely not the party paying for
   * the freight, which is why it is its own field and not the client's name.
   * `cargoDescription` is the one-line answer to "what is this shipment", the
   * thing that goes on the offer above the position table; the positions
   * underneath carry their own per-item descriptions and are entered on the
   * cargo tab. Both are header columns on `quotes` and are written by the header
   * write, which is why they are here and not on a cargo position. */
  cargoOwner: string
  cargoDescription: string
  /** FR-4. Where the goods are collected — the start of the lane the client
   *  asked about, before any route option offers one. */
  pickupLocation: string
  incoterm: string
  paymentTerms: string
  /** S-12: the credit term's day count, asked only while `paymentTerms` is
   *  `CREDIT` (`isCreditTerms`). A string because that is what an input
   *  yields; blank is "not stated", which the server refuses on a quote whose
   *  terms are CHANGED to credit and accepts on a legacy credit quote. */
  paymentTermsCreditDays: string
  /* FR-11's deal terms: what a delay costs, and how delivery actually happens.
   *
   * `demurrageTerms` is what a standing box costs once the carrier's free time
   * has run out — free text, because the real ones are sentences ("USD 80/day
   * after freetime, per container") rather than a rate the platform computes
   * with.
   *
   * `deliveryTerms` is NOT the Incoterm and the form must not let the two blur:
   * the Incoterm (FR-59) is the ICC three-letter rule that assigns risk and
   * cost, and this is our own sentence about how delivery actually happens
   * ("door to door", "to the bonded warehouse"). A quote can carry either, both
   * or neither. */
  demurrageTerms: string
  deliveryTerms: string
  /* FR-4's optional carrier/supplier DISPLAY line, and the second clause of that
   * criterion is the load-bearing one: it is informational only and assigns no
   * vendor to any cost line. 0006's own column comment says the same, and
   * TestAnonTheCarrierLineIsTextAndNotAParticipantReference pins that it never
   * resolves to a participant id.
   *
   * Free text on purpose, therefore, and deliberately NOT a directory pick: a
   * picker over participants would make this look like the vendor field it must
   * not be, and the first person to use it that way would be attaching an agent
   * to a quote in a column no suppression rule covers (FR-29, AD-6). */
  carrierDisplay: string
  /* FR-23. The client's required transit time, as two numbers.
   *
   * Held as strings because that is what an input yields, and converted once at
   * the payload boundary — the same shape CargoFormSheet uses for its
   * dimensions. Storing "20-25 days" as text is what FR-23 rules out. */
  transitDaysMin: string
  transitDaysMax: string
  /* FR-13. The header measures the salesperson may disagree with.
   *
   * Strings for the AD-9 reason the cargo measures are, and BLANK is the
   * meaningful default: it means "the sum over the positions stands", which is
   * the ordinary case. Typing a figure here is a human overriding arithmetic,
   * usually off a weighbridge ticket; emptying the field again returns the
   * header to the sum. */
  grossWeightKgOverride: string
  volumeCbmOverride: string
  chargeableWeightKg: string
  /** FR-12. The volumes are the client's rather than our estimate. */
  finalPackingListReceived: boolean
  /* The INTERNAL colleagues on this quote (migration 0028), as user ids.
   *
   * Staff accounts only. A quote names no EXTERNAL parties (a shipper, a
   * consignee, a driver) since S-22a dropped its participants table (migration
   * 0075): a counterparty appears where it acts, on the case. This list is the
   * create's own — team membership is what grants visibility, so it is written
   * in the create's transaction rather than by a second request that can fail
   * on its own.
   *
   * Ids rather than draft objects, because there is nothing to type: a
   * colleague is PICKED, and their name and role come from the account.
   * That also makes the local `key` unnecessary — a user id is already stable
   * and unique within the list.
   *
   * The owner is absent from this list and is not added here: the server seeds
   * them onto their own team inside the create transaction, so a client that
   * also sent them would be guessing at the caller's own id. */
  team: string[]
}

/* The party draft and its validators used to live here — a local key, a role
 * that started blank, and the `%_@_%` check on a party who asked to be emailed.
 * They are gone with the card that filled them, and since S-22a no screen types
 * a case or quote party at all: both participant lists were dropped (migration
 * 0075), and a counterparty is picked where it acts.
 */

/** A whole number, or blank. Empty is legitimate: transit time is optional. */
export function isBlankOrCount(raw: string): boolean {
  const normalised = raw.trim()
  return normalised === '' || /^\d+$/.test(normalised)
}

/** The stored payment-terms value that carries a day count (S-12) — one of
 *  `paymentOptionValues`, matched as the server's `ClassifyPaymentTerms` does:
 *  trimmed, exactly. */
export const CREDIT_TERMS = 'CREDIT'

export function isCreditTerms(terms: string | undefined): boolean {
  return (terms ?? '').trim() === CREDIT_TERMS
}

/** The credit days the server accepts — a whole number from 1 to 365 — or
 *  blank, which the form leaves for the server to judge (a legacy credit quote
 *  may keep none; a quote changed to credit may not). */
export function isBlankOrCreditDays(raw: string): boolean {
  const normalised = raw.trim()
  if (normalised === '') return true
  if (!/^\d+$/.test(normalised)) return false
  const days = Number(normalised)
  return days >= 1 && days <= 365
}

/** The day count as the wire takes it: the number, or `null` for blank. */
export function creditDaysPayload(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw.trim())
}

/** A decimal measure, or blank. A comma is accepted because that is what a
 *  Russian keyboard produces, and the payload normalises it to a dot. */
export function isBlankOrDecimal(raw: string): boolean {
  const normalised = raw.replace(/\s/g, '').replace(',', '.')
  return normalised === '' || /^\d+(\.\d+)?$/.test(normalised)
}

/** Today, date-only (AD-19). A quote defaults to the day it is written. */
export function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10)
}

/** A blank header, so both entries to UJ-1 start from the same defaults. */
export const EMPTY_QUOTE_DRAFT: QuoteDraft = {
  clientId: '',
  clientContactId: '',
  quoteDate: '',
  validUntil: '',
  cargoOwner: '',
  cargoDescription: '',
  pickupLocation: '',
  incoterm: '',
  paymentTerms: '',
  paymentTermsCreditDays: '',
  demurrageTerms: '',
  deliveryTerms: '',
  carrierDisplay: '',
  transitDaysMin: '',
  transitDaysMax: '',
  grossWeightKgOverride: '',
  volumeCbmOverride: '',
  chargeableWeightKg: '',
  finalPackingListReceived: false,
  /* Nobody but the owner by default, and the owner is the server's to add. Most
     quotes are opened by one person for themselves; pre-filling a colleague
     would be guessing at who the deal is with. */
  team: [],
}

/** An optional wire number as the form's string field.
 *
 *  Absent stays EMPTY rather than becoming "0": the transit range and the free
 *  time are optional, and a zero-day transit is a claim nobody made. */
function numberField(value: number | undefined): string {
  return value === undefined ? '' : String(value)
}

/* A saved quote as the header draft, for a surface that EDITS rather than
 * creates.
 *
 * Extracted from QuoteFormSheet because four surfaces now need it: the sheet,
 * the two builder disclosures that amend the header after creation, and step 1
 * of the builder revisited on a quote that already exists — which without this
 * showed the six answers already given as an empty form. Two copies of this
 * mapping would drift exactly as the validators did before this module existed
 * — and the failure mode is worse here, because a field this function forgets
 * is a field the next whole-record PUT sends as empty.
 *
 * Every field is prefilled from the record, and that is a correction rather
 * than an improvement. Several of them used to be reset to '' or false on the
 * edit branch, which was harmless only while the sheet was mounted by nothing:
 * PUT /quotes/:id takes the whole record and reads an empty field as "clear
 * it", so the first header edit anybody made would have wiped the payment
 * terms, the Incoterm, the transit range, all three measure overrides and the
 * FR-12 packing-list flag — fields the form displays, so the user would have
 * watched their own values vanish.
 *
 * The measure overrides come from cargoTotals rather than from the header,
 * because that is where the wire carries them: the three `*Override` fields are
 * the values AS ENTERED, added for exactly this prefill. `effective*` would be
 * wrong to use — on a quote with no override it is the SUM, so resubmitting it
 * would turn arithmetic into a human's correction.
 */
export function draftFromRecord(record: QuoteDetail): QuoteDraft {
  return {
    // Spread first, so a field added to the draft is carried here rather than
    // dropped from the edit branch alone.
    ...EMPTY_QUOTE_DRAFT,
    clientId: record.clientId,
    quoteDate: record.quoteDate ?? todayDateOnly(),
    validUntil: record.validUntil ?? '',
    clientContactId: record.clientContactId ?? '',
    cargoOwner: record.cargoOwner ?? '',
    cargoDescription: record.cargoDescription ?? '',
    pickupLocation: record.pickupLocation ?? '',
    incoterm: record.incoterm ?? '',
    paymentTerms: record.paymentTerms ?? '',
    paymentTermsCreditDays: numberField(record.paymentTermsCreditDays),
    demurrageTerms: record.demurrageTerms ?? '',
    deliveryTerms: record.deliveryTerms ?? '',
    carrierDisplay: record.carrierDisplay ?? '',
    transitDaysMin: numberField(record.transitDaysMin),
    transitDaysMax: numberField(record.transitDaysMax),
    grossWeightKgOverride: record.cargoTotals?.grossWeightKgOverride ?? '',
    volumeCbmOverride: record.cargoTotals?.volumeCbmOverride ?? '',
    chargeableWeightKg: record.cargoTotals?.chargeableWeightKgOverride ?? '',
    finalPackingListReceived: record.cargoTotals?.finalPackingListReceived ?? false,
    /* Empty on the EDIT path, and deliberately so. The header is what these
       surfaces edit; the internal team is edited on its own card, against a
       quote that already exists. Empty is silence rather than deletion here
       because toQuoteUpdatePayload omits the key when it is empty — it says so
       itself, having been written for exactly this shared create/update use. */
    team: [],
  }
}

/** Everything the create endpoint refuses, in one place so a form's own error
 *  hints and the button that submits them cannot disagree about whether the
 *  header is finished. */
export function quoteDraftInvalid(draft: QuoteDraft): boolean {
  const transitInverted =
    draft.transitDaysMin !== '' &&
    draft.transitDaysMax !== '' &&
    Number(draft.transitDaysMin) > Number(draft.transitDaysMax)

  return (
    !draft.clientId ||
    !isBlankOrCount(draft.transitDaysMin) ||
    !isBlankOrCount(draft.transitDaysMax) ||
    transitInverted ||
    !isBlankOrDecimal(draft.grossWeightKgOverride) ||
    !isBlankOrDecimal(draft.volumeCbmOverride) ||
    !isBlankOrDecimal(draft.chargeableWeightKg) ||
    (isCreditTerms(draft.paymentTerms) && !isBlankOrCreditDays(draft.paymentTermsCreditDays))
  )
}
